import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Disable caching for all requests
axios.defaults.headers.common['Cache-Control'] = 'no-cache, no-store, must-revalidate';
axios.defaults.headers.common['Pragma'] = 'no-cache';
axios.defaults.headers.common['Expires'] = '0';

// Profile Image Upload
export const uploadProfileImage = (file) => {
    const formData = new FormData();
    formData.append('file', file);
    // Note: Authorization header is set via axios.defaults in AuthContext
    return axios.post(`${API}/auth/profile/image`, formData);
};
export const deleteProfileImage = () => axios.delete(`${API}/auth/profile/image`);

// Dashboard - with cache buster
export const getDashboardOverview = () => axios.get(`${API}/dashboard/overview`, { 
    params: { _t: Date.now() }
});

// Nutrition
export const addFoodEntry = (data) => axios.post(`${API}/nutrition/food`, data);
export const getFoodEntries = (date) => axios.get(`${API}/nutrition/food`, { params: { date } });
export const updateFoodEntry = (id, data) => axios.put(`${API}/nutrition/food/${id}`, data);
export const deleteFoodEntry = (id) => axios.delete(`${API}/nutrition/food/${id}`);
export const addWaterEntry = (data) => axios.post(`${API}/nutrition/water`, data);
export const getWaterEntries = (date) => axios.get(`${API}/nutrition/water`, { params: { date } });
export const getNutritionSummary = (date) => axios.get(`${API}/nutrition/summary`, { params: { date } });
export const getNutritionPeriodSummary = (days) => axios.get(`${API}/nutrition/period-summary`, { params: { days } });
export const analyzeFood = (data) => axios.post(`${API}/nutrition/analyze`, data);
export const analyzeFoodImage = (imageBase64) => axios.post(`${API}/nutrition/analyze/image`, { image_base64: imageBase64 });
export const lookupBarcode = (ean) => axios.get(`${API}/nutrition/barcode/${ean}`);

// Sleep
export const addSleepEntry = (data) => axios.post(`${API}/sleep`, data);
export const getSleepEntries = (days) => axios.get(`${API}/sleep`, { params: { days } });
export const updateSleepEntry = (id, data) => axios.put(`${API}/sleep/${id}`, data);
export const deleteSleepEntry = (id) => axios.delete(`${API}/sleep/${id}`);
export const getSleepSummary = (days) => axios.get(`${API}/sleep/summary`, { params: { days } });

// Fitness
export const addWorkout = (data) => axios.post(`${API}/fitness/workouts`, data);
export const getWorkouts = (days) => axios.get(`${API}/fitness/workouts`, { params: { days } });
export const updateWorkout = (id, data) => axios.put(`${API}/fitness/workouts/${id}`, data);
export const deleteWorkout = (id) => axios.delete(`${API}/fitness/workouts/${id}`);
export const getFitnessSummary = (days) => axios.get(`${API}/fitness/summary`, { params: { days } });
export const estimateWorkoutCalories = (workout_type, duration_minutes) => 
    axios.post(`${API}/fitness/estimate-calories`, null, { params: { workout_type, duration_minutes } });

// Tasks
export const createTask = (data) => axios.post(`${API}/tasks`, data);
export const getTasks = (completed) => axios.get(`${API}/tasks`, { params: { completed } });
export const updateTask = (id, data) => axios.put(`${API}/tasks/${id}`, data);
export const completeTask = (id) => axios.post(`${API}/tasks/${id}/complete`);
export const deleteTask = (id) => axios.delete(`${API}/tasks/${id}`);

// Travel
export const createTrip = (data) => axios.post(`${API}/travel/trips`, data);
export const getTrips = () => axios.get(`${API}/travel/trips`);
export const updateTrip = (id, data) => axios.put(`${API}/travel/trips/${id}`, data);
export const getCurrentTrip = () => axios.get(`${API}/travel/current`);
export const deleteTrip = (id) => axios.delete(`${API}/travel/trips/${id}`);

// Health Symptoms
export const addSymptom = (data) => axios.post(`${API}/health/symptoms`, data);
export const getSymptoms = (date, days) => axios.get(`${API}/health/symptoms`, { params: { date, days } });
export const updateSymptom = (id, data) => axios.put(`${API}/health/symptoms/${id}`, data);
export const getSymptomsSummary = (days) => axios.get(`${API}/health/symptoms/summary`, { params: { days } });
export const getSymptomCorrelations = (days = 30) => axios.get(`${API}/health/correlations`, { params: { days } });
export const getHealthAlerts = () => axios.get(`${API}/health/alerts`);
export const getWeeklyTrends = () => axios.get(`${API}/health/weekly-trends`);
export const deleteSymptom = (id) => axios.delete(`${API}/health/symptoms/${id}`);

// Push Notifications
export const subscribeToPush = (subscription) => axios.post(`${API}/notifications/subscribe`, subscription);
export const unsubscribeFromPush = () => axios.delete(`${API}/notifications/unsubscribe`);
export const getNotificationStatus = () => axios.get(`${API}/notifications/status`);

// Gamification
export const getGamificationStats = () => axios.get(`${API}/gamification/stats`);
export const getLeaderboard = () => axios.get(`${API}/gamification/leaderboard`);

// AI Coach
export const chatWithCoach = (message, context) => axios.post(`${API}/coach/chat`, { message, context });
export const getDailyMotivation = () => {
    // Append cached geo coords (set by WeatherTipsCard / WeatherBriefCard) so
    // Nanouk's morning greeting can reference today's weather.
    let params = '';
    try {
        const raw = localStorage.getItem('bestbuddy_geo');
        if (raw) {
            const { coords, ts } = JSON.parse(raw);
            if (coords && ts && Date.now() - ts < 12 * 60 * 60 * 1000) {
                params = `?lat=${coords.lat}&lon=${coords.lon}`;
            }
        }
    } catch { /* ignore */ }
    return axios.get(`${API}/coach/motivation${params}`);
};
export const getNutritionAdvice = (question, context) => axios.post(`${API}/nutrition/advice`, { question, context });
export const getReadinessScore = () => axios.get(`${API}/coach/readiness`);

// Reports & Export
export const getWeeklyReport = (language) => axios.get(`${API}/reports/weekly`, { params: { language } });
export const exportHealthPDF = async (language = 'de', period = 'week') => {
    const response = await axios.get(`${API}/reports/export/pdf`, {
        params: { language, period },
        responseType: 'blob'
    });
    return response;
};
export const getActiveChallenge = (language) => axios.get(`${API}/reports/challenge/active`, { params: { language } });
export const acceptChallenge = (challengeId) => axios.post(`${API}/reports/challenge/accept`, { challenge_id: challengeId });

// Daily Insights
export const getDailyInsights = (language) => axios.get(`${API}/insights/daily`, { params: { language } });


// Extreme-weather push trigger (fired by Dashboard once coords are available)
export const triggerWeatherExtremeCheck = (lat, lon, language) =>
    axios.post(`${API}/coach/weather-extreme-check`, { lat, lon, language });

// Mood
export const logMood = (data) => axios.post(`${API}/mood`, data);
export const getTodayMood = () => axios.get(`${API}/mood/today`);
export const getMoodHistory = (days) => axios.get(`${API}/mood/history`, { params: { days } });

// Notifications
export const getNotifications = () => axios.get(`${API}/notifications/history`);
export const getUnreadNotificationCount = () => axios.get(`${API}/notifications/unread-count`);
export const markNotificationsRead = (ids) => axios.post(`${API}/notifications/mark-read`, { notification_ids: ids });
export const getNotificationPreferences = () => axios.get(`${API}/notifications/preferences`);
export const updateNotificationPreferences = (prefs) => axios.post(`${API}/notifications/preferences`, prefs);
export const sendTestNotification = () => axios.post(`${API}/notifications/test`);


// Coach FAB unread badge (smart-insight pushes since last /coach visit)
export const getCoachUnreadCount = () => axios.get(`${API}/coach/unread-count`);

// AI status indicator (lightweight ping; backend caches for 60s)
export const getAiStatus = (force = false) =>
    axios.get(`${API}/system/ai-status${force ? '?force=true' : ''}`);

export const markCoachInsightsRead = () => axios.post(`${API}/coach/mark-read`);
